import type {Textbox} from '@postermywall/fabricjs-2';
import {LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import type {TextLineStylesObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import {getDefaultLineList, getListTypeForTypedPrefix} from '@PosterWhiteboard/items/text-item/text-line-list.library';
import {applyListToTextboxSelection} from '@PosterWhiteboard/items/text-item/text-style-runs-compiler';
import {BULLETS_FONT_FAMILY, isFontLoaded, loadBulletsFont} from '@/libraries/font-library';

/**
 * Auto-bullet typing for an edit session, shared by text items and the slideshow's text slides:
 * "- ", "* " or "1. " typed at the start of a plain line converts the line into a list row (the
 * prefix is removed and the caret lands at the row start), matching standard editors.
 *
 * Only an INSERTION that completes the prefix converts. The text change that reaches
 * {@link convert} comes from every path that edits the textbox — on-canvas typing, the mobile
 * text field, deletions — so the text is compared with the text of the previous change: a
 * change that made it shorter is a deletion, and backspacing "- Apple" down to "- " must leave
 * the row alone (it turned it into a bullet before 2026-10-12).
 */
export class TypedListPrefixConversion {
  private textAtPreviousChange: string | null = null;
  private isReplayAfterBulletsFontLoad = false;

  /** Called when an edit session starts, so the first change is compared with the session's opening text. */
  public startSession(textbox: Textbox): void {
    this.textAtPreviousChange = textbox.text ?? '';
    this.isReplayAfterBulletsFontLoad = false;
  }

  /**
   * Converts the caret's line when the change just inserted the prefix. Returns whether a
   * conversion ran (the caller then commits the edited text). A bullet marker must never draw
   * before its face: an unordered prefix keeps its text until the bullets font is in, then
   * `replayTextChangeAfterBulletsFontLoads` re-runs the keystroke's text change, which converts
   * regardless of growth.
   */
  public convert(textbox: Textbox, lineStyles: Record<string, TextLineStylesObject>, replayTextChangeAfterBulletsFontLoads: () => void): boolean {
    const text = textbox.text ?? '';
    const isInsertion = this.isReplayAfterBulletsFontLoad || text.length > (this.textAtPreviousChange ?? text).length;
    this.textAtPreviousChange = text;
    this.isReplayAfterBulletsFontLoad = false;
    if (!isInsertion) {
      return false;
    }
    const caret = textbox.selectionStart;
    if (caret !== textbox.selectionEnd) {
      return false;
    }
    const {lineIndex, charIndex} = textbox.get2DCursorLocation(caret, true);
    const lineText = text.split('\n')[lineIndex] ?? '';
    const listType = getListTypeForTypedPrefix(lineText.slice(0, charIndex));
    if (listType === null || lineStyles[lineIndex]?.list) {
      return false;
    }
    if (listType === LIST_TYPES.UNORDERED && !isFontLoaded(BULLETS_FONT_FAMILY)) {
      loadBulletsFont(BULLETS_FONT_FAMILY, () => {
        this.isReplayAfterBulletsFontLoad = true;
        replayTextChangeAfterBulletsFontLoads();
      });
      return false;
    }
    const lineStartGrapheme = caret - charIndex;
    textbox.removeChars(lineStartGrapheme, lineStartGrapheme + charIndex);
    textbox.selectionStart = lineStartGrapheme;
    textbox.selectionEnd = lineStartGrapheme;
    if (textbox.hiddenTextarea) {
      textbox.hiddenTextarea.value = textbox.text ?? '';
      textbox.hiddenTextarea.selectionStart = lineStartGrapheme;
      textbox.hiddenTextarea.selectionEnd = lineStartGrapheme;
    }
    applyListToTextboxSelection(textbox, getDefaultLineList(listType), lineStartGrapheme, lineStartGrapheme);
    this.textAtPreviousChange = textbox.text ?? '';
    return true;
  }
}
